import qs from "qs";
import { API_BASE_URL, AUDIO_DOWNLOAD_PATH } from "@/shared/config/env";
import { normalizeLanguageOptions, normalizeTimeZoneOptions } from "@/shared/lib/localeOptions";
import { ensureFreshToken, http } from "./httpClient";
import type {
  AsteriskNSS,
  BreakDTO,
  ConfigRow,
  ConfigType,
  IceServers,
  Macros,
  PageParams,
  PageResponse,
  Phone,
  PhoneActivity,
  Queue,
  ReportQuery,
  Role,
  SnowSyncStats,
  Tag,
  User,
  UserActivity,
  UserRequestDto,
  UserResponseDto,
} from "./types";

function apiUrl(path: string) {
  return `${API_BASE_URL}${path}`;
}

async function authHeaders() {
  const token = await ensureFreshToken();
  if (!token) throw new Error("unauthenticated");
  return { Authorization: `Bearer ${token}` };
}

/** Remove null/undefined/"" para não enviar filtros vazios. */
function compact(obj: Record<string, unknown> | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!obj) return out;
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = v;
  }
  return out;
}

/**
 * Monta os parâmetros Spring Pageable. `page` é 1-based na UI e 0-based no backend.
 * Os filtros extras NÃO podem conter page/size/sortBy/sortDesc (seriam sobrescritos / vazariam).
 */
export function buildPageParams(
  page: number,
  size: number,
  sortBy?: string[] | null,
  sortDesc?: boolean[] | null,
  extra?: Record<string, unknown>,
) {
  const rest = { ...compact(extra) };
  delete rest.page;
  delete rest.size;
  delete rest.sortBy;
  delete rest.sortDesc;
  delete rest.sort;
  const opt: Record<string, unknown> = { ...rest, page: Math.max(0, (page || 1) - 1), size: size || 10 };
  if (sortBy?.length) {
    opt.sort = sortBy.map((col, i) => `${col},${sortDesc?.[i] ? "desc" : "asc"}`);
  }
  return opt;
}

const repeatSerializer = (p: Record<string, unknown>) => qs.stringify(p, { arrayFormat: "repeat" });

async function getJson<T>(path: string, params?: Record<string, unknown>) {
  const headers = await authHeaders();
  const { data } = await http.get<T>(apiUrl(path), { params, headers });
  return data;
}

async function getPaged<T>(path: string, p: PageParams, extra?: Record<string, unknown>) {
  const headers = await authHeaders();
  const { data } = await http.get<PageResponse<T>>(apiUrl(path), {
    params: buildPageParams(p.page, p.size, p.sortBy, p.sortDesc, extra),
    paramsSerializer: repeatSerializer,
    headers,
  });
  return data;
}

type FormValue = string | boolean | number | null | undefined;

function toFormData(fields: Record<string, FormValue>) {
  const body = new FormData();
  for (const [k, v] of Object.entries(fields)) {
    if (v != null) body.append(k, String(v));
  }
  return body;
}

async function postForm<T = unknown>(path: string, fields: Record<string, FormValue> | FormData) {
  const headers = await authHeaders();
  const body = fields instanceof FormData ? fields : toFormData(fields);
  const { data } = await http.post<T>(apiUrl(path), body, { headers });
  return data;
}

async function putForm<T = unknown>(path: string, fields: Record<string, FormValue>) {
  const headers = await authHeaders();
  const { data } = await http.put<T>(apiUrl(path), toFormData(fields), { headers });
  return data;
}

async function del<T = unknown>(path: string, params: Record<string, unknown>) {
  const headers = await authHeaders();
  const { data } = await http.delete<T>(apiUrl(path), { params, headers });
  return data;
}

/** Filtros de relatório → query params aceitos pelo LogController. */
function reportFilters(q: ReportQuery) {
  return {
    user: q.user,
    extension: q.extension,
    info: q.info,
    callid: q.callid,
    sysid: q.sysid,
    durationFrom: q.durationFrom,
    durationTo: q.durationTo,
    from: q.from,
    to: q.to,
  };
}

/** Normaliza a resposta text/plain de /recordingUrl (aceita também JSON legado {url}). */
function extractUrl(data: unknown): string {
  if (typeof data === "string") return data.trim().replace(/^"|"$/g, "");
  if (data && typeof data === "object" && "url" in data) return String((data as { url: unknown }).url ?? "");
  return "";
}

export const phonemanagerApi = {
  /* ——— Usuários ——— */

  getUsers: (p: PageParams & { name?: string | null }) =>
    getPaged<User>("/rest/user/users", p, { name: p.name }),

  getUser: (name: string) => getJson<User>("/rest/user/user", { name }),

  deleteUser: (name: string) => del("/rest/user/user", { name }),

  /** Legado multipart (PUT /rest/user/user). Preferir `insertUser`. */
  createUser: (fields: Record<string, string | boolean>) => putForm<User>("/rest/user/user", fields),

  /** PUT /rest/user/{id} (JSON UserRequestDto). Enviar supervisor/roles/tags/userBreaks completos. */
  updateUser: async (id: number | string, payload: UserRequestDto) => {
    const headers = await authHeaders();
    const { data } = await http.put(apiUrl(`/rest/user/${id}`), payload, {
      headers: { ...headers, "Content-Type": "application/json" },
    });
    return data as UserResponseDto;
  },

  /** POST /rest/user (JSON UserRequestDto). */
  insertUser: async (payload: UserRequestDto) => {
    const headers = await authHeaders();
    const { data } = await http.post(apiUrl("/rest/user"), payload, {
      headers: { ...headers, "Content-Type": "application/json" },
    });
    return data as UserResponseDto;
  },

  getRoles: () => getJson<string[]>("/rest/user/roles"),

  loadOptionsRole: () => getJson<Role[]>("/rest/role/findAll"),

  loadOptionsTag: () => getJson<Tag[]>("/rest/tag/findAll"),

  /** GET /breaks/findAll — pausas disponíveis para vincular ao usuário. */
  loadOptionsBreaks: () => getJson<BreakDTO[]>("/breaks/findAll"),

  getAllUsers: () => getJson<UserResponseDto[]>("/rest/user/findAll"),

  changeUserPassword: (name: string, password: string) =>
    postForm<User>("/rest/user/changePassword", { name, password }),

  changeUserPasswordByLogin: (password: string) =>
    postForm<User>("/rest/user/changePasswordByLogin", { password }),

  /** Retorna contadores Map<String,Integer> (phoneManagerUsers, snowUsers, ...). */
  syncWithSnow: () => getJson<SnowSyncStats>("/rest/user/syncWithSnow"),

  forceLogout: (username: string) => postForm("/rest/log/phone/forceLogout", { username }),

  /* ——— Locale ——— */

  getTimeZones: async () => normalizeTimeZoneOptions(await getJson<unknown>("/rest/conf/timeZones")),

  getSupportedLanguages: async () =>
    normalizeLanguageOptions(await getJson<unknown>("/rest/conf/supportedLanguages")),

  /** Retorna TZChildren {name, displayName, str, gmt, offset}. */
  getUserTimeZone: () => getJson<unknown>("/rest/user/timezoneByLogin"),

  setUserTimeZone: (timezone: string) => postForm("/rest/user/timezoneByLogin", { timezone }),

  getLanguageByLogin: () => getJson<string>("/rest/user/languageByLogin"),

  setLanguageByLogin: (language: string) => postForm("/rest/user/languageByLogin", { language }),

  /* ——— Filas ——— */

  getQueues: (p: PageParams) => getPaged<Queue>("/rest/infra/queues", p),

  getQueues2: () => getJson<Queue[]>("/rest/infra/queues2"),

  createQueue: (queueNumber: string, name?: string, description?: string) =>
    putForm<Queue>("/rest/infra/queue", { queueNumber, name, description }),

  updateQueue: (queueNumber: string, name?: string, description?: string) =>
    postForm<Queue>("/rest/infra/queue", { queueNumber, name, description }),

  deleteQueue: (queueNumber: string) => del("/rest/infra/queue", { queueNumber }),

  /* ——— Tags ——— */

  getTags: (p: PageParams) => getPaged<Tag>("/rest/user/tags", p),

  getTags2: () => getJson<Tag[]>("/rest/user/tags2"),

  createTag: (name: string, description?: string) => putForm<Tag>("/rest/user/tag", { name, description }),

  updateTag: (name: string, description?: string) => postForm<Tag>("/rest/user/tag", { name, description }),

  deleteTag: (name: string) => del("/rest/user/tag", { name }),

  addTagToTheUser: (userName: string, tagName: string) => postForm("/rest/user/userTag", { userName, tagName }),

  removeTagFromUser: (userName: string, tagName: string) => del("/rest/user/userTag", { userName, tagName }),

  /* ——— Ramais ——— */

  getPhone: (phoneNumber: string) => getJson<Phone>("/rest/infra/phone", { phoneNumber }),

  deletePhone: (phoneNumber: string) => del("/rest/infra/phone", { phoneNumber }),

  /** PUT /rest/infra/allocatePhone (multipart username) — retorna o Phone alocado. */
  allocatePhone: (username: string) => putForm<Phone>("/rest/infra/allocatePhone", { username }),

  addPhoneToTheQueue: (phoneNumber: string, queueNumber: string) =>
    postForm<Phone>("/rest/infra/phoneQueue", { phoneNumber, queueNumber }),

  removePhoneFromQueue: (phoneNumber: string, queueNumber: string) =>
    del<Phone>("/rest/infra/phoneQueue", { phoneNumber, queueNumber }),

  /**
   * POST /rest/infra/phone (multipart). iceServers/macros/breaks são @RequestPart de objeto,
   * enviados como JSON string (igual ao GUI) — o MultipartConfigurer do backend desserializa via Jackson.
   */
  updatePhone: (fields: {
    phoneNumber: string;
    wsServer?: string | null;
    sipServer?: string | null;
    automaticAnswering?: boolean | null;
    iceServers?: IceServers | null;
    macros?: Macros | null;
    breaks?: Macros | null;
  }) => {
    const body = new FormData();
    body.append("phoneNumber", fields.phoneNumber);
    if (fields.wsServer != null) body.append("wsServer", fields.wsServer);
    if (fields.sipServer != null) body.append("sipServer", fields.sipServer);
    if (fields.automaticAnswering != null) body.append("automaticAnswering", String(fields.automaticAnswering));
    if (fields.iceServers != null) body.append("iceServers", JSON.stringify(fields.iceServers));
    if (fields.macros != null) body.append("macros", JSON.stringify(fields.macros));
    if (fields.breaks != null) body.append("breaks", JSON.stringify(fields.breaks));
    return postForm<Phone>("/rest/infra/phone", body);
  },

  /* ——— Configurações estáticas ——— */

  getConfigs: (p: PageParams) => getPaged<ConfigRow>("/rest/conf/configs", p),

  getConfig: (name: string) => getJson<ConfigRow>("/rest/conf/config", { name }),

  /** PUT /rest/conf/config — `configType` é obrigatório no backend. */
  createConfig: (name: string, configType: ConfigType | string, value?: string, description?: string) =>
    putForm<ConfigRow>("/rest/conf/config", { name, configType, value, description }),

  updateConfig: (name: string, value?: string, description?: string) =>
    postForm<ConfigRow>("/rest/conf/config", { name, value, description }),

  deleteConfig: (name: string) => del("/rest/conf/config", { name }),

  overrideConfigForAllPhonesAndUsers: (name: string) => postForm("/rest/conf/configOverride", { name }),

  /* ——— Relatórios ——— */

  getPhoneActivityMinStarted: () => getJson<string>("/rest/log/phone/minDate"),

  /** Relatório de atividade — filtro de ramal vai como `phoneNumber` neste endpoint. */
  findPhoneActivities: (q: ReportQuery) => {
    const { extension, ...f } = reportFilters(q);
    return getPaged<PhoneActivity>("/rest/log/phone/activity", q, { ...f, phoneNumber: extension });
  },

  getUserActivityMinStarted: () => getJson<string>("/rest/log/user/minDate"),

  /** Relatório de agente — filtro de ramal é `extension`. */
  findUserActivities: (q: ReportQuery) => {
    const { callid: _c, sysid: _s, ...f } = reportFilters(q);
    void _c;
    void _s;
    return getPaged<UserActivity & PhoneActivity>("/rest/log/user/activity", q, f);
  },

  getClientActivityMinStarted: () => getJson<string>("/rest/log/client/minDate"),

  /** Relatório de cliente — filtro de ramal é `extension`. */
  findClientActivities: (q: ReportQuery) =>
    getPaged<PhoneActivity>("/rest/log/client/activity", q, reportFilters(q)),

  /** Lista de partes da gravação (valores reais, ex.: ["1","2"]). */
  getRecordingParts: async (callId: string) => {
    const data = await getJson<unknown>("/rest/log/recordingParts", { callId });
    return Array.isArray(data) ? data.map((p) => String(p)) : [];
  },

  /** GET /rest/log/recordingUrl (text/plain) → URL assinada da gravação. */
  getAudioUrl: async (callId: string, part?: string | null) => {
    const headers = await authHeaders();
    const { data } = await http.get(apiUrl("/rest/log/recordingUrl"), {
      params: compact({ callId, part }),
      headers: { ...headers, Accept: "text/plain, application/json" },
      responseType: "text",
    });
    return extractUrl(data);
  },

  /** Modo SEFAZ: baixa o áudio como Blob (GET /rest/log/recording/download). */
  getDownloadAudio: async (callId: string, part?: string | null) => {
    const headers = await authHeaders();
    const { data } = await http.get<Blob>(apiUrl(AUDIO_DOWNLOAD_PATH), {
      params: compact({ callId, part }),
      headers: { ...headers, Accept: "audio/*, application/octet-stream" },
      responseType: "blob",
    });
    return data;
  },

  getAsteriskNSSMinCreatedAt: () => getJson<string>("/rest/log/asterisk/nns/minDate"),

  findAsteriskNSS: (q: ReportQuery) =>
    getPaged<AsteriskNSS>("/rest/log/asterisk/nss/activity", q, {
      callid: q.callid,
      sysid: q.sysid,
      from: q.from,
      to: q.to,
    }),

  findAsteriskNSSByCallId: (callId: string) => getJson<AsteriskNSS>("/rest/log/asterisk/nns", { callId }),

  getBreaksToFilter: () => getJson<unknown>("/rest/status/getBreaksToFilter"),
};
